/**
 * The order screen: menu on one side, the running tab on the other.
 *
 * This is where a waiter spends the shift, so the frame is fixed and only the
 * menu moves. The tab stays on screen while the menu scrolls under the thumb,
 * and the total never moves — it is the last thing read aloud before payment.
 *
 * In portrait the tab drops to a drawer along the bottom edge, still fixed,
 * still showing the total. A carried tablet is portrait more often than the
 * layout used to admit.
 *
 * WHAT THE NEXT DISH GOES ON AS
 *
 * Course, seat and "with a note" apply to whatever is tapped next, and they
 * stay put: a waiter takes the starters, moves to course two, and takes the
 * mains. Asking per dish would put a sheet in front of every tap, and the
 * common table is one course, no seats and no notes — so they are one line
 * over the dishes ("Course 1 · Table"), opened when they change, rather than
 * a row of pickers always on screen.
 *
 * THE DISHES ARE THE TAB
 *
 * The tab is mostly a list of what the guest ordered, and every dish stays on
 * it — sent, cooking, up or voided — under a quiet heading for its course.
 * A line carries no buttons; tapping it opens what can be done to it, one line
 * at a time. The tab's own panel keeps two buttons, Send and Pay, under the
 * total; everything done less often to a whole tab is behind More. A tab used
 * to be five full-width buttons and a row of links with the order squeezed
 * between them, and in portrait the order did not show at all.
 *
 * WHY A COURSE HAS TO BE SENT
 *
 * On a plan with course firing, a table's dishes wait on the kitchen screen,
 * held and with no clock, until somebody sends their course. Until this screen
 * had the button, nothing could — every Professional dish sat held for good.
 * The Send button under the total sends the earliest course waiting; a later
 * one can be sent from its own heading.
 *
 * Every price shown comes from the server. The browser does no arithmetic on
 * money: a second implementation of the pricing rules would eventually
 * disagree with the first, in front of a guest.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  describeUnavailable,
  formatMoney,
  groupCategories,
  trimQty,
  useAddLine,
  useApplyCoupon,
  useApplyDiscount,
  useCancelOrder,
  useChangeLineQuantity,
  useDiscountReasons,
  useEntitlements,
  useFireCourse,
  useMenu,
  usePermission,
  useHandOver,
  useOnDuty,
  useOrder,
  usePriceQuote,
  useRemoveCoupon,
  useRemoveDiscount,
  useRemoveServiceCharge,
  useRestoreServiceCharge,
  useSetOrderNote,
  useSetTakeaway,
  useVoidLine,
  useFloor,
  useCheckRequested,
  type MenuItem,
  type Order,
  type OrderCoupon,
  type OrderDiscount,
  type OrderLine,
  type OrderServiceCharge,
} from '@restaurant/api-client';
import {
  ActionError,
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Segmented,
  Sheet,
} from '@restaurant/ui';

import { timeOf, useFoodUp } from './readiness';
import {
  groupTab,
  lineDetail,
  lineMark,
  nextToSend,
  showCourseHeads,
  type CourseGroup,
  type LineMark,
} from './tab-lines';
import { orderName, useTableLabels } from './order-name';
import { PayScreen } from './pay-screen';
import { GuestSheet, GuestStrip } from './guest';
import { useGuestsOnTill, usePointsOnTill } from './guest-access';
import { PointsSheet } from './points';
import { GuestsSheet, HistorySheet, MoveTableSheet } from './tab-tools';

/**
 * "Eat in or take away?" — asked at a counter at any point up to paying.
 *
 * It changes tax and nothing a guest pays: in many countries a dish sits in a
 * different band in a bag, and the server moves each line to its dish's
 * takeaway class, or back. The total does not move, which is why nothing on
 * this screen changes but the choice — and why it needs no confirmation.
 */
function EatInOrTakeAway({ order }: { order: Order }) {
  const set = useSetTakeaway();
  return (
    <div className="os__takeaway">
      <Segmented
        label="Eat in or take away"
        value={order.takeaway ? 'away' : 'in'}
        onChange={(value) => set.mutate({ orderId: order.id, takeaway: value === 'away' })}
        options={[
          { value: 'in', label: 'Eat in' },
          { value: 'away', label: 'Take away' },
        ]}
      />
      <ActionError error={set.error} action="Taking an order away" />
    </div>
  );
}

/**
 * The reasons a line actually gets voided, as staff say them.
 *
 * A void needs a reason, and the moment it happens is the only time anyone
 * knows what it was — but typing one on a tablet mid-service means raising the
 * keyboard over half the screen, which is how "asdf" ends up in the takings
 * report. Four taps cover almost every case; the fifth opens a field for the
 * one that does not.
 */
/** The second row's "every category in this group". Not a category id. */
const ALL = '__all__';

const VOID_REASONS = [
  'Sent back',
  'Wrong item',
  'Guest changed their mind',
  'Kitchen error',
] as const;

export function OrderScreen({
  orderId,
  locationId,
  onClose,
  backLabel = 'Orders',
}: {
  orderId: string;
  locationId: string | undefined;
  onClose: () => void;
  /** Where leaving goes back to. A counter order did not come from the tabs. */
  backLabel?: string;
}) {
  const order = useOrder(orderId);
  const menu = useMenu(locationId ? { locationId } : {});
  const addLine = useAddLine();
  const foodUp = useFoodUp([orderId]);
  const fire = useFireCourse();

  const [category, setCategory] = useState<string | null>(null);
  // Inside a group ("Juices" in "Drinks"); null is the whole group.
  const [section, setSection] = useState<string | null>(null);
  const [pending, setPending] = useState<MenuItem | null>(null);
  const [paying, setPaying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [noting, setNoting] = useState(false);
  const [tool, setTool] = useState<'guests' | 'guest' | 'move' | 'history' | null>(null);
  const [more, setMore] = useState(false);
  const [picking, setPicking] = useState(false);
  // The one line whose actions are showing. One at a time: two open lines is
  // two sets of Void buttons, one of them under the wrong dish.
  const [openLine, setOpenLine] = useState<string | null>(null);
  const guestsOn = useGuestsOnTill();
  const tableLabel = useTableLabels(locationId);
  const mayMove = usePermission('floor.table.transfer');
  const mayTakePayment = usePermission('payments.payment.take');
  const [discounting, setDiscounting] = useState<'bill' | null>(null);
  const mayDiscount = usePermission('pricing.discount.apply');
  const [couponing, setCouponing] = useState(false);
  const mayCoupon = usePermission('pricing.coupon.redeem');
  const [spendingPoints, setSpendingPoints] = useState(false);
  const pointsOn = usePointsOnTill();
  const mayRedeem = usePermission('customers.loyalty.redeem');
  const mayCancel = usePermission('orders.order.cancel');
  const mayFire = usePermission('orders.course.fire');
  const entitlements = useEntitlements();
  const firingPlan = entitlements.has('orders.course_firing');
  const seatsOn = entitlements.has('orders.seats');
  const couponsOn = entitlements.has('pricing.coupons');

  // What the next dish goes on as. Kept between taps on purpose — see the top.
  const [course, setCourse] = useState(1);
  const [seat, setSeat] = useState<number | null>(null);
  const [withNote, setWithNote] = useState(false);

  // The top row is the groups and the categories in none; a group's own
  // categories are a second row under it. One level, as the server keeps it.
  const menuGroups = useMemo(() => groupCategories(menu.data?.categories ?? []), [menu.data]);
  const menuGroup = menuGroups.find((g) => g.category.id === category) ?? menuGroups[0];
  const inside = useMemo(() => menuGroup?.inside ?? [], [menuGroup]);
  const picked = inside.some((c) => c.id === section) ? section : null;
  const items = useMemo(() => {
    if (!menuGroup) return [];
    // The whole group reads in menu order: its own dishes, then each
    // category inside it in turn.
    const shown = picked ? [picked] : [menuGroup.category.id, ...inside.map((c) => c.id)];
    return (menu.data?.items ?? [])
      .filter((item) => shown.includes(item.category_id))
      .sort((a, b) => shown.indexOf(a.category_id) - shown.indexOf(b.category_id));
  }, [menu.data, menuGroup, inside, picked]);

  if (order.isPending || menu.isPending) return <Loading label="Opening the tab" />;
  if (order.isError) return <ErrorNotice message={order.error.message} />;
  if (menu.isError) return <ErrorNotice message={menu.error.message} />;

  function choose(item: MenuItem) {
    if (!item.is_available) return;
    // Anything with a choice to make opens the sheet; everything else goes
    // straight on, because the common case is one tap.
    if (withNote || item.variants.length > 0 || item.modifier_groups.length > 0) {
      setPending(item);
      return;
    }
    addLine.mutate({ orderId, item_id: item.id, course_no: course, seat_no: seat });
  }

  const live = order.data;
  // A counter order goes to the kitchen as it is rung in, on any plan (see
  // `kds.handlers.on_line_added`), so nothing on it waits to be sent and
  // saying "Not sent" on it was wrong.
  const firing = firingPlan && live.service_mode !== 'counter';
  // What meal deals took, said by the server: the discount total now also
  // holds promotions and a coupon, and the browser does not add up money.
  const dealTook = Number(live.meal_deal_total);
  // Promotions off dishes, one row each. A coupon worth an amount is its own
  // row with the coupon, below.
  const promotionRows = live.promotions.filter((promo) => promo.promotion_id !== null);

  const billable = live.lines.filter((l) => l.state !== 'voided');
  const groups = groupTab(live.lines, firing, foodUp(orderId));
  const headed = showCourseHeads(groups, firing);
  // The one Send under the total sends the earliest course still waiting.
  // Only for whoever may send one; anybody else reads "Waiting to be sent"
  // on the course's heading, as before.
  const sendNext = firing && mayFire ? nextToSend(groups) : null;
  const seats = seatsOn ? Math.min(Math.max(live.guest_count, 1), 20) : 0;

  // Everything done to a whole tab less often than Send and Pay, in one list
  // behind More. Each is offered on exactly the terms its button had.
  const moreActions: MoreAction[] = [
    ...(guestsOn && !live.customer_id
      ? [
          {
            key: 'guest',
            short: 'Guest',
            label: 'Put a guest on this tab',
            hint: 'Who it is for, with what they cannot eat.',
            onPick: () => setTool('guest'),
          },
        ]
      : []),
    ...(live.table_id && mayMove
      ? [
          {
            key: 'move',
            short: 'Move table',
            label: 'Move to another table',
            hint: 'The dishes and what is owed go with it.',
            onPick: () => setTool('move'),
          },
        ]
      : []),
    {
      key: 'note',
      short: 'Note',
      label: live.note ? 'Change the note' : 'Add a note',
      hint: 'Shown at the top of every kitchen docket for this tab.',
      onPick: () => setNoting(true),
    },
    {
      key: 'history',
      label: 'History',
      hint: 'What has happened to this tab, and who did it.',
      onPick: () => setTool('history'),
    },
    ...(mayDiscount && billable.length > 0
      ? [
          {
            key: 'discount',
            short: 'Money off',
            label: 'Money off the bill',
            hint: 'Against one of the restaurant’s reasons. For one dish, tap the dish.',
            onPick: () => setDiscounting('bill'),
          },
        ]
      : []),
    // One coupon per bill, so it goes once one is on.
    ...(mayCoupon && couponsOn && live.coupons.length === 0
      ? [
          {
            key: 'coupon',
            label: 'Coupon',
            hint: 'Take a code the guest shows. One per bill.',
            onPick: () => setCouponing(true),
          },
        ]
      : []),
    // The guest's own points, spent by whoever is serving them. Only with a
    // guest named: points belong to somebody.
    ...(pointsOn && mayRedeem && live.customer_id && billable.length > 0
      ? [
          {
            key: 'points',
            label: 'Use points',
            hint: 'Spend the guest’s points on this bill.',
            onPick: () => setSpendingPoints(true),
          },
        ]
      : []),
    // A section changes hands at the end of a shift and is covered at a
    // break, and the tab goes with it. Offered whatever the plan — the price
    // is said when it is tapped, not hidden so nobody finds out the till can
    // do it.
    {
      key: 'handover',
      label: 'Hand over',
      hint: 'Give this tab to a colleague. What is owed stays as it is.',
      onPick: () => setHandingOver(true),
    },
    // The only way out of a tab that should not exist. Voiding needs lines,
    // so a tab opened by mistake and never ordered on had no exit at all: it
    // stayed open, held its table, and sat in Tabs for the rest of the shift.
    // Last, and in red, where a thumb lands on it least by accident.
    ...(mayCancel
      ? [
          {
            key: 'cancel',
            label: 'Cancel this tab',
            hint: 'For a tab that should not exist. Asks why first.',
            onPick: () => setCancelling(true),
            danger: true,
          },
        ]
      : []),
  ];
  const everydayActions = moreActions.filter((action) => action.short);
  const rareActions = moreActions.filter((action) => !action.short);

  if (paying) {
    return (
      <PayScreen
        orderId={orderId}
        locationId={locationId}
        order={live}
        name={orderName(live, tableLabel)}
        // Said at the moment the money is taken, because that is when the
        // cashier tells them what to listen for and the only screen that
        // knows it. Null on a table, which is nobody's number to call.
        collectionNumber={live.service_mode === 'counter' ? live.number : null}
        onSettled={onClose}
        onBack={() => setPaying(false)}
      />
    );
  }

  return (
    <div className="os">
      <section className="os__menu">
        <header className="os__menu-head">
          <Button variant="ghost" onClick={onClose}>
            ← {backLabel}
          </Button>
          <Segmented
            label="Menu categories"
            scroll
            value={menuGroup?.category.id ?? null}
            onChange={(id) => {
              setCategory(id);
              setSection(null);
            }}
            options={menuGroups.map((g) => ({ value: g.category.id, label: g.category.name }))}
          />
          {/* Over the dishes, not over the tab: it describes the next tap on
              this side. One line, opened when it changes — in portrait the
              tab is a capped drawer whose list was already fought for. */}
          <AddingTo
            course={course}
            counter={live.service_mode === 'counter'}
            seat={seat}
            seats={seats}
            withNote={withNote}
            onOpen={() => setPicking(true)}
          />
        </header>

        {/* A group's categories: "All" first, so one tap on Drinks already
            shows every drink and the second row only narrows it. */}
        {menuGroup && inside.length > 0 && (
          <div className="os__sections">
            <Segmented
              label={`Inside ${menuGroup.category.name}`}
              scroll
              value={picked ?? ALL}
              onChange={(id) => setSection(id === ALL ? null : id)}
              options={[
                { value: ALL, label: 'All' },
                ...inside.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>
        )}

        {/* A new restaurant opens its first tab before it has a menu: a blank
            grid read as the till being broken, so it says where dishes come
            from. */}
        {(menu.data?.items.length ?? 0) === 0 ? (
          <div className="os__menu-empty">
            <Empty
              title="Nothing on the menu yet"
              description="Dishes and prices are added in the back office, under Menu. They show up here as soon as they are saved."
            />
          </div>
        ) : (
          items.length === 0 && (
            <div className="os__menu-empty">
              <Empty title="Nothing in this category" description="Pick another one above." />
            </div>
          )
        )}

        <div className="os__grid">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`dish${item.is_available ? '' : ' is-off'}`}
              onClick={() => choose(item)}
              disabled={!item.is_available}
              aria-label={
                item.is_available
                  ? `${item.name}, ${formatMoney(item.price)}`
                  : `${item.name}, ${describeUnavailable(item.unavailable_reasons)}`
              }
            >
              {/* The photo helps a new starter find the dish; the name is still
                  the thing read, so the picture stays small. */}
              {item.image && (
                <img className="dish__img" src={item.image.thumb} alt="" loading="lazy" />
              )}
              <span className="dish__name">{item.name}</span>
              <span className="dish__foot">
                <span className="dish__price tabular">{formatMoney(item.price)}</span>
                {item.is_available && item.modifier_groups.length > 0 && (
                  <span className="dish__tag">options</span>
                )}
              </span>
              {/* An off item stays on screen, struck through. Removing it would
                  make a waiter think they mis-tapped rather than that the
                  kitchen has run out. */}
              {!item.is_available && (
                <span className="dish__off">
                  {item.eightysix_reason ?? describeUnavailable(item.unavailable_reasons)}
                </span>
              )}
            </button>
          ))}
        </div>

        {addLine.isError && (
          <div className="os__menu-error">
            <ActionError
              error={addLine.error}
              action="This way of ordering"
              title="That did not go on"
            />
          </div>
        )}
      </section>

      <aside className="os__tab" aria-label={`Tab #${live.number}`}>
        <header className="os__tab-head">
          <span className="os__number">{orderName(live, tableLabel)}</span>
          {/* A counter order is one person: there is no cover count to change. */}
          {live.service_mode !== 'counter' && (
            <button
              type="button"
              className="os__guests os__guests-btn"
              onClick={() => setTool('guests')}
              aria-label={`${live.guest_count} guests, change`}
            >
              {live.guest_count} guest{live.guest_count === 1 ? '' : 's'}
            </button>
          )}
          {/* Said in the head because the choice itself is behind More, and
              "is this one to take away?" is asked at the counter. */}
          {live.takeaway && <span className="os__away">Take away</span>}
          {rareActions.length > 0 && (
            <Button
              variant="ghost"
              className="os__more-btn"
              onClick={() => setMore(true)}
              aria-haspopup="dialog"
              aria-label={`More for tab #${live.number}`}
            >
              More ⋯
            </Button>
          )}
        </header>

        {everydayActions.length > 0 && (
          <div className="os__tools" role="toolbar" aria-label="This tab">
            {everydayActions.map((action) => (
              <button
                key={action.key}
                type="button"
                className="os__tool"
                onClick={action.onPick}
              >
                {action.short}
              </button>
            ))}
          </div>
        )}

        {/* Who it is for, with what they cannot eat, above everything else
            on the tab: the moment it matters is the next dish rung in. */}
        {guestsOn && live.customer_id && (
          <GuestStrip order={live} onOpen={() => setTool('guest')} />
        )}

        {live.note && <p className="os__tab-note">{live.note}</p>}

        <div className="os__lines">
          {live.lines.length === 0 ? (
            <Empty title="Nothing on it yet" description="Tap a dish to add it." />
          ) : (
            groups.map((group) => (
              <section
                key={group.courseNo}
                className="os__course"
                aria-label={`Course ${group.courseNo}`}
              >
                {headed && (
                  <CourseHead
                    orderId={orderId}
                    group={group}
                    mayFire={mayFire}
                    // The earliest waiting course is sent from under the
                    // total; its heading only says it is waiting.
                    sendHere={sendNext !== null && sendNext.courseNo !== group.courseNo}
                  />
                )}
                {group.lines.map((line) => (
                  <TabLine
                    key={line.id}
                    orderId={orderId}
                    order={live}
                    line={line}
                    firing={firing}
                    mark={lineMark(line, group, firing)}
                    open={openLine === line.id}
                    onOpen={setOpenLine}
                  />
                ))}
              </section>
            ))
          )}
        </div>

        {/* The total is the last thing a waiter reads out. It never moves. */}
        <footer className="os__total">
          {/* A meal deal is detected rather than rung in, so the lines add up
              to more than the total and nothing on the tab says why. That is
              the question a guest asks at the counter about once a service,
              and without this line the waiter has no answer either. Shown
              only when there is one, so an ordinary tab keeps one row. */}
          {(Number(live.discount_total) !== 0 || live.service_charge !== null) && (
            <div className="os__total-row os__total-row--minor">
              <span>Lines</span>
              <span className="tabular">{formatMoney(live.subtotal)}</span>
            </div>
          )}
          {Number(live.discount_total) !== 0 && (
            <>
              {/* What a deal took is what is left once every discount somebody
                  chose is accounted for. Shown apart from them, because one is
                  a rule and the others are decisions with a name against
                  them. */}
              {dealTook > 0 && (
                <div className="os__total-row os__total-row--minor os__discount">
                  <span>Meal deal</span>
                  <span className="tabular">−{formatMoney(live.meal_deal_total)}</span>
                </div>
              )}
              {/* Named as the guest will read it on the bill. Applied by the
                  server from the back office's rules: nothing to undo here. */}
              {promotionRows.map((promo) => (
                <div
                  key={`${promo.promotion_id}:${promo.code ?? ''}`}
                  className="os__total-row os__total-row--minor os__discount"
                >
                  <span>
                    {promo.name}
                    {promo.code && ` (${promo.code})`}
                  </span>
                  <span className="tabular">−{formatMoney(promo.amount)}</span>
                </div>
              ))}
              {live.discounts.map((discount) => (
                <DiscountRow key={discount.id} orderId={orderId} discount={discount} />
              ))}
            </>
          )}
          {/* A coupon stays on the tab while it takes nothing, with the
              server's reason, so "add a dessert and it works" can be seen to
              work. */}
          {live.coupons.map((coupon) => (
            <CouponRow key={coupon.id} orderId={orderId} coupon={coupon} />
          ))}
          {/* Its own line, above the total and under what came off: it is
              worked out on the bill after its discounts, and a guest asking
              why the total is more than the dishes gets the answer here. */}
          {live.service_charge && (
            <ServiceChargeRow orderId={orderId} charge={live.service_charge} />
          )}
          <div className="os__total-row">
            <span>Total</span>
            {/* Server-calculated. The browser never adds up money. */}
            <span className="os__total-value tabular">{formatMoney(live.total)}</span>
          </div>
          {fire.isError && <ActionError error={fire.error} action="Sending a course" />}
          <div className={`os__go${sendNext ? ' os__go--two' : ''}`}>
            {sendNext && (
              <Button
                variant="primary"
                size="lg"
                block
                loading={fire.isPending}
                onClick={() => fire.mutate({ orderId, courseNo: sendNext.courseNo })}
                aria-label={`${sendLabel(sendNext, groups.length > 1)}: course ${sendNext.courseNo} to the kitchen, ${sendNext.held} waiting`}
              >
                {sendLabel(sendNext, groups.length > 1)}
              </Button>
            )}
            {/* While something waits to be sent, sending is the next thing to
                do, so Pay steps back a shade rather than competing with it. */}
            {mayTakePayment ? (
              <Button
                variant={sendNext ? 'secondary' : 'primary'}
                size="lg"
                block
                disabled={billable.length === 0}
                onClick={() => setPaying(true)}
              >
                Pay
              </Button>
            ) : (
              // Somebody who cannot take money was shown the whole payment
              // screen and refused at the last tap. What they can do is tell
              // whoever takes it that the table wants the bill.
              <BillAsked tableId={live.table_id} locationId={locationId} />
            )}
          </div>
        </footer>
      </aside>

      {more && (
        <MoreSheet
          order={live}
          actions={rareActions}
          // Only off a table: a tab at a table is eaten there. Offered where
          // the plan has takeaway tax classes, and on an order already taken
          // away whatever the plan, so it can always be put back.
          takeaway={
            live.table_id === null && (entitlements.has('pricing.tax.classes') || live.takeaway)
          }
          onDone={() => setMore(false)}
        />
      )}

      {picking && (
        <AddingSheet
          course={course}
          counter={live.service_mode === 'counter'}
          onCourse={setCourse}
          seat={seat}
          onSeat={setSeat}
          seats={seats}
          withNote={withNote}
          onWithNote={setWithNote}
          onDone={() => setPicking(false)}
        />
      )}

      {discounting === 'bill' && (
        <DiscountSheet order={live} onDone={() => setDiscounting(null)} />
      )}
      {couponing && <CouponSheet order={live} onDone={() => setCouponing(false)} />}

      {spendingPoints && <PointsSheet order={live} onDone={() => setSpendingPoints(false)} />}

      {handingOver && (
        <HandOverSheet
          order={live}
          locationId={locationId}
          onDone={() => setHandingOver(false)}
        />
      )}

      {cancelling && (
        <CancelTabSheet
          order={live}
          onDone={() => setCancelling(false)}
          onCancelled={onClose}
        />
      )}

      {noting && <TabNoteSheet order={live} onDone={() => setNoting(false)} />}
      {tool === 'guests' && <GuestsSheet order={live} onDone={() => setTool(null)} />}
      {tool === 'guest' && <GuestSheet order={live} onDone={() => setTool(null)} />}
      {tool === 'move' && (
        <MoveTableSheet order={live} locationId={locationId} onDone={() => setTool(null)} />
      )}
      {tool === 'history' && <HistorySheet order={live} onDone={() => setTool(null)} />}

      {pending && (
        <ChoiceSheet
          item={pending}
          orderId={orderId}
          locationId={locationId}
          course={course}
          seat={seat}
          askNote={withNote}
          onDone={() => {
            setPending(null);
            // A note is about one dish. Leaving the toggle on would put the
            // next ten through a sheet nobody asked for.
            setWithNote(false);
          }}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

/**
 * In place of Pay, for somebody who cannot take money: at a table, say that
 * it has asked for the bill, which lights it up on every till's room for
 * whoever can take it. Off a table there is nothing to mark, so it says
 * where the money is taken.
 */
function BillAsked({
  tableId,
  locationId,
}: {
  tableId: string | null;
  locationId: string | undefined;
}) {
  const floor = useFloor(tableId ? locationId : undefined);
  const ask = useCheckRequested();
  const table = (floor.data ?? []).flatMap((plan) => plan.tables).find((t) => t.id === tableId);

  if (!tableId) {
    return <p className="os__bill-note">A cashier takes the payment.</p>;
  }
  const asked = table?.state === 'check_requested' || table?.state === 'paying';
  return (
    <>
      <Button
        variant="secondary"
        size="lg"
        block
        disabled={asked || !table}
        loading={ask.isPending}
        onClick={() => ask.mutate({ tableId })}
      >
        {asked ? 'Bill asked for' : 'They asked for the bill'}
      </Button>
      <ActionError error={ask.error} action="Asking for the bill" />
    </>
  );
}

/** What the Send button under the total says, kept short enough to sit beside Pay. */
function sendLabel(group: CourseGroup, severalCourses: boolean): string {
  if (group.held < group.live) return `Send ${group.held} more`;
  return severalCourses ? `Send course ${group.courseNo}` : 'Send to kitchen';
}

interface MoreAction {
  key: string;
  /**
   * Set on the few used on most tabs: they sit on the tab as plain buttons,
   * under this name, and stay out of More. Everything else is a few times a
   * shift at most, and waits behind More.
   */
  short?: string;
  label: string;
  hint: string;
  onPick: () => void;
  danger?: boolean;
}

/**
 * Everything else that can be done to the whole tab, in one list.
 *
 * These were five full-width buttons under the total and four links in the
 * head, and between them they left the order itself a sliver. They are each
 * used a few times a shift at most; Send and Pay are used on every tab. A tap
 * here closes this list and opens the action's own sheet, which asks exactly
 * what it asked before.
 */
function MoreSheet({
  order,
  actions,
  takeaway,
  onDone,
}: {
  order: Order;
  actions: MoreAction[];
  takeaway: boolean;
  onDone: () => void;
}) {
  return (
    <Sheet title="More" onDismiss={onDone}>
      {/* A choice between two with the current one showing, and it changes
          nothing the guest pays, so it is answered here without a sheet of
          its own. */}
      {takeaway && <EatInOrTakeAway order={order} />}
      <ul className="os__more" aria-label="What to do with this tab">
        {actions.map((action) => (
          <li key={action.key}>
            <button
              type="button"
              className={`os__more-item${action.danger ? ' os__more-item--danger' : ''}`}
              onClick={() => {
                onDone();
                action.onPick();
              }}
            >
              <span className="os__more-label">{action.label}</span>
              <span className="os__more-hint">{action.hint}</span>
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}

function TabLine({
  orderId,
  order,
  line,
  firing,
  mark,
  open,
  onOpen,
}: {
  orderId: string;
  order: Order;
  line: OrderLine;
  firing: boolean;
  mark: LineMark;
  /** Whether this line's actions are showing. */
  open: boolean;
  /** Opens a line by id, or closes whichever is open with null. */
  onOpen: (lineId: string | null) => void;
}) {
  const [voiding, setVoiding] = useState(false);
  const [discounting, setDiscounting] = useState(false);
  const mayDiscount = usePermission('pricing.discount.apply');
  // The server refuses a void to anybody without it, so the button is not
  // offered to them: a refusal after the reason is typed is the worst way to
  // find out.
  const mayVoid = usePermission('orders.line.void');
  const changeQty = useChangeLineQuantity();
  const rowRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLButtonElement>(null);
  const actsId = useId();
  const voided = line.state === 'voided';
  const off = Number(line.line_discount);
  const qty = Number(line.qty);
  // A line with money off keeps its quantity: the discount was worked out on
  // it. Fewer than one is a void, which asks why.
  const stepping = !voided && off === 0 && Number.isInteger(qty);
  // Once the kitchen has a dish some may already be made, so fewer is a void.
  const inKitchen = line.state === 'fired' || !firing;
  const step = (by: number) =>
    changeQty.mutate({ orderId, lineId: line.id, qty: String(qty + by) });
  const showing = open && !voided;

  // Tapping anywhere else closes it — another line, a dish, the total — and
  // so does Escape, handing focus back to the line. A sheet opened from the
  // line sits inside it, so working in one does not close the line under it.
  useEffect(() => {
    if (!showing) return;
    function away(event: PointerEvent) {
      if (event.target instanceof Node && rowRef.current?.contains(event.target)) return;
      onOpen(null);
    }
    function escape(event: KeyboardEvent) {
      if (event.key !== 'Escape' || voiding || discounting) return;
      onOpen(null);
      mainRef.current?.focus();
    }
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [showing, onOpen, voiding, discounting]);

  const detail = lineDetail(line);
  const body: ReactNode = (
    <>
      <span className="line__qty tabular">{trimQty(line.qty)}×</span>
      <span className="line__text">
        <span className="line__name">
          {line.name}
          {line.variant_name && <span className="line__variant"> {line.variant_name}</span>}
        </span>
        {(detail !== '' || line.note) && (
          <span className="line__detail">
            {detail}
            {detail !== '' && line.note && ' · '}
            {line.note && <span className="line__note">“{line.note}”</span>}
          </span>
        )}
        {/* What a promotion took off this dish, by its name: the guest asking
            why the cocktail was cheaper gets "Happy hour" as the answer. */}
        {line.promotions.map((promo) => (
          <span key={`${promo.promotion_id}:${promo.code ?? ''}`} className="line__off">
            {promo.name} −{formatMoney(promo.amount)}
          </span>
        ))}
        {off > 0 && <span className="line__off">Money off −{formatMoney(off.toFixed(2))}</span>}
        {voided && <span className="line__void">Voided — {line.void_reason}</span>}
      </span>
      <span className="line__side">
        <span className="line__total tabular">{formatMoney(line.line_total)}</span>
        <Mark mark={mark} />
      </span>
    </>
  );

  return (
    <div
      ref={rowRef}
      className={`line${voided ? ' is-voided' : ''}${showing ? ' is-open' : ''}`}
    >
      {voided ? (
        // Nothing can be done to a voided line, so it is not a button.
        <div className="line__main">{body}</div>
      ) : (
        <button
          ref={mainRef}
          type="button"
          className="line__main"
          aria-expanded={showing}
          aria-controls={showing ? actsId : undefined}
          onClick={() => onOpen(open ? null : line.id)}
        >
          {body}
        </button>
      )}

      {changeQty.isError && (
        <span className="line__error" role="alert">
          {changeQty.error.message}
        </span>
      )}

      {showing && (
        <div
          id={actsId}
          className="line__acts"
          role="group"
          aria-label={`${line.name}: change`}
        >
          {stepping && (
            <span className="line__step" role="group" aria-label={`How many ${line.name}`}>
              {!inKitchen && (
                <button
                  type="button"
                  className="line__act line__step-btn"
                  disabled={qty <= 1 || changeQty.isPending}
                  onClick={() => step(-1)}
                  aria-label={`One fewer ${line.name}`}
                >
                  −
                </button>
              )}
              <button
                type="button"
                className="line__act line__step-btn"
                disabled={changeQty.isPending}
                onClick={() => step(1)}
                aria-label={`One more ${line.name}`}
              >
                +
              </button>
            </span>
          )}
          {mayDiscount && off === 0 && (
            <button
              type="button"
              className="line__act"
              onClick={() => setDiscounting(true)}
              aria-label={`Money off ${line.name}`}
            >
              Money off
            </button>
          )}
          {mayVoid && (
            <button
              type="button"
              className="line__act line__act--danger"
              onClick={() => setVoiding(true)}
              aria-label={`Void ${line.name}`}
            >
              Void
            </button>
          )}
        </div>
      )}

      {voiding && <VoidSheet orderId={orderId} line={line} onDone={() => setVoiding(false)} />}
      {discounting && (
        <DiscountSheet order={order} line={line} onDone={() => setDiscounting(false)} />
      )}
    </div>
  );
}

/**
 * The small mark at the end of a line: where the dish is. Words, not a dot —
 * a held dish and a sent one differ by whether somebody walks to the pass, and
 * a colour alone is not enough to say that on a dim floor.
 */
function Mark({ mark }: { mark: LineMark }) {
  if (mark === null || mark === 'voided') return null;
  if (mark === 'up') return <span className="up line__up">Up</span>;
  return (
    <span className={`line__mark${mark === 'held' ? ' line__mark--held' : ''}`}>
      {mark === 'held' ? 'Not sent' : 'Sent'}
    </span>
  );
}

function VoidSheet({
  orderId,
  line,
  onDone,
}: {
  orderId: string;
  line: OrderLine;
  onDone: () => void;
}) {
  const voidLine = useVoidLine();
  const [reason, setReason] = useState<string | null>(null);
  const [other, setOther] = useState('');

  const chosen = reason === 'other' ? other.trim() : (reason ?? '');

  return (
    <Sheet
      title={`Void ${line.name}?`}
      tone="danger"
      onDismiss={onDone}
      actions={
        <>
          {/* The safe choice first and the destructive one last, where a thumb lands by accident least often. */}
          <Button size="lg" block onClick={onDone}>
            Keep it
          </Button>
          <Button
            variant="danger"
            size="lg"
            block
            disabled={chosen === ''}
            loading={voidLine.isPending}
            onClick={() =>
              voidLine.mutate(
                { orderId, lineId: line.id, reason: chosen },
                { onSuccess: onDone },
              )
            }
          >
            Void {formatMoney(line.line_total)}
          </Button>
        </>
      }
    >
      <p className="os__void-note">
        A void needs a reason. This is the only moment anyone knows what it was.
      </p>

      <div className="os__reasons">
        {VOID_REASONS.map((option) => (
          <button
            key={option}
            type="button"
            className={`os__reason${reason === option ? ' is-on' : ''}`}
            aria-pressed={reason === option}
            onClick={() => setReason(option)}
          >
            {option}
          </button>
        ))}
        <button
          type="button"
          className={`os__reason${reason === 'other' ? ' is-on' : ''}`}
          aria-pressed={reason === 'other'}
          onClick={() => setReason('other')}
        >
          Something else
        </button>
      </div>

      {reason === 'other' && (
        <Field label="What happened?">
          <Input
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder="Spilled on the way out"
            autoFocus
            maxLength={200}
          />
        </Field>
      )}

      {voidLine.isError && (
        <ErrorNotice title="That was not voided" message={voidLine.error.message} />
      )}
    </Sheet>
  );
}

/**
 * Variant and modifier picker.
 *
 * A sheet from the bottom edge rather than a centred dialog: it puts "Add to
 * tab" where the thumb already is, and it keeps that button above the keyboard
 * on the rare occasion one opens.
 */
function ChoiceSheet({
  item,
  orderId,
  locationId,
  course,
  seat,
  askNote,
  onDone,
  onCancel,
}: {
  item: MenuItem;
  orderId: string;
  locationId: string | undefined;
  course: number;
  seat: number | null;
  /** Opened for the note, so the field has the cursor rather than the sizes. */
  askNote: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const addLine = useAddLine();
  const [note, setNote] = useState('');
  const [variantId, setVariantId] = useState<string | null>(
    item.variants.find((v) => v.is_default)?.id ?? item.variants[0]?.id ?? null,
  );
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  function toggle(optionId: string, group: { max_select: number | null }) {
    setChosen((previous) => {
      const next = new Set(previous);
      if (next.has(optionId)) {
        next.delete(optionId);
        return next;
      }
      // A single-choice group replaces rather than accumulates, which is what
      // "how would you like it cooked" means.
      if (group.max_select === 1) {
        for (const option of item.modifier_groups.flatMap((g) =>
          g.max_select === 1 && g.options.some((o) => o.id === optionId) ? g.options : [],
        )) {
          next.delete(option.id);
        }
      }
      next.add(optionId);
      return next;
    });
  }

  const unmet = item.modifier_groups.filter(
    (group) => group.min_select > 0 && !group.options.some((o) => chosen.has(o.id)),
  );

  // What this dish comes to with the size and options chosen, asked of the
  // server as they change: the browser does no arithmetic on money, and a
  // price book or a site's own price would make a local sum wrong anyway.
  const quote = usePriceQuote();
  const { mutate: askPrice } = quote;
  const chosenKey = [...chosen].sort().join(',');
  useEffect(() => {
    askPrice({
      item_id: item.id,
      variant_id: variantId,
      modifier_option_ids: chosenKey === '' ? [] : chosenKey.split(','),
      location_id: locationId ?? null,
    });
  }, [askPrice, item.id, variantId, chosenKey, locationId]);
  const price = quote.data && !quote.isPending ? formatMoney(quote.data.unit_price) : null;

  return (
    <Sheet
      title={item.name}
      onDismiss={onCancel}
      actions={
        <>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={unmet.length > 0}
            loading={addLine.isPending}
            onClick={() =>
              addLine.mutate(
                {
                  orderId,
                  item_id: item.id,
                  variant_id: variantId,
                  modifier_option_ids: [...chosen],
                  course_no: course,
                  seat_no: seat,
                  note: note.trim() === '' ? null : note.trim(),
                },
                { onSuccess: onDone },
              )
            }
          >
            {unmet.length > 0
              ? `Choose ${unmet[0]?.name}`
              : price
                ? `Add to tab · ${price}`
                : 'Add to tab'}
          </Button>
          {addLine.isError && (
            <p className="os__sheet-error" role="alert">
              {addLine.error.message}
            </p>
          )}
        </>
      }
    >
      {item.variants.length > 0 && (
        <section className="os__group">
          <p className="os__group-title">Size</p>
          <div className="os__options">
            {item.variants.map((variant) => (
              <button
                key={variant.id}
                type="button"
                className={`os__option${variant.id === variantId ? ' is-on' : ''}`}
                aria-pressed={variant.id === variantId}
                onClick={() => setVariantId(variant.id)}
              >
                {variant.name}
                {variant.price_delta !== '0.00' && (
                  <span className="os__option-price tabular">
                    {formatMoney(variant.price_delta)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {item.modifier_groups.map((group) => (
        <section key={group.id} className="os__group">
          <p className="os__group-title">
            {group.prompt ?? group.name}
            {group.min_select > 0 && <Badge tone="warn">required</Badge>}
          </p>
          <div className="os__options">
            {group.options.map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={!option.is_available}
                className={`os__option${chosen.has(option.id) ? ' is-on' : ''}`}
                aria-pressed={chosen.has(option.id)}
                onClick={() => toggle(option.id, group)}
              >
                {option.name}
                {option.price_delta !== '0.00' && (
                  <span className="os__option-price tabular">
                    {formatMoney(option.price_delta)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      {/* On every dish, not only when asked for: "no onions" is said while
          the waiter is already choosing the cook, and should not need the
          sheet closing and reopening. */}
      <Field label="For the kitchen" hint="Goes with this dish to whichever screen cooks it.">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="No nuts"
          maxLength={240}
          autoFocus={askNote}
        />
      </Field>
    </Sheet>
  );
}

const CANCEL_REASONS = ['Opened by mistake', 'Duplicate tab', 'They walked out'] as const;

/**
 * Cancelling a whole tab.
 *
 * Not the same act as voiding every line. A void is a line a guest did not get;
 * this is a tab that should never have existed, and it is the only exit from an
 * empty one — nothing can be voided on a tab with no lines, and a £0 bill takes
 * no payment, so without this the tab stays open and holds its table until
 * somebody closes it in the database.
 *
 * A tab with food already fired is a different matter, and the sheet says so:
 * the kitchen has it, and cancelling here does not reach into the pass.
 */
function HandOverSheet({
  order,
  locationId,
  onDone,
}: {
  order: Order;
  locationId: string | undefined;
  onDone: () => void;
}) {
  const handOver = useHandOver();
  const colleagues = useOnDuty(locationId);

  // Whoever has it now is not somebody to hand it to.
  const others = (colleagues.data ?? []).filter((person) => person.user_id !== order.served_by);

  return (
    <Sheet title="Hand this tab over" onDismiss={onDone}>
      <p className="os__sheet-note">
        Who has the table now. What they ordered, what has been fired and what is owed all stay
        as they are — only who is looking after it changes.
      </p>

      {colleagues.isPending && <Loading label="Finding who is on" />}

      {colleagues.isError && <ErrorNotice message={colleagues.error.message} />}

      {colleagues.data && others.length === 0 && (
        <p className="os__sheet-note">
          Nobody else here has a login. Somebody needs one before a tab can be handed to them.
        </p>
      )}

      <ul className="os__handover">
        {others.map((person) => (
          <li key={person.user_id}>
            <button
              type="button"
              className="os__colleague"
              disabled={handOver.isPending}
              onClick={() =>
                handOver.mutate(
                  { orderId: order.id, toUserId: person.user_id },
                  { onSuccess: onDone },
                )
              }
            >
              <span className="os__colleague-name">{person.name}</span>
              {/* Said in words, not only by position in the list: who is
                  actually in the building is the thing being decided on. */}
              <span className="os__colleague-where">
                {person.on_shift ? 'on shift' : 'not clocked in'}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <ActionError error={handOver.error} action="Handing the tab over" />
    </Sheet>
  );
}

/**
 * Money off, at the till.
 *
 * The amount is worked out by the server, not here: a percentage the browser
 * rounds and a percentage the bill rounds are two answers to what a guest
 * pays. So this sends "20%" or "£5" and shows what comes back.
 *
 * Reasons are the restaurant's own list. One that needs a note says so before
 * the button is pressed rather than after, and a ceiling is shown against the
 * reason, because "manager only, up to 20%" is a rule somebody is trying to
 * follow rather than a trap.
 */
function DiscountSheet({
  order,
  line,
  onDone,
}: {
  order: Order;
  line?: OrderLine | undefined;
  onDone: () => void;
}) {
  const reasons = useDiscountReasons();
  const apply = useApplyDiscount();
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');

  const chosen = reasons.data?.find((reason) => reason.code === code);
  // Off the dishes, never off the service charge: the charge is worked out
  // on what is left after this, so it comes down with it.
  const base = line
    ? Number(line.line_total) -
      Number(line.combo_discount) -
      Number(line.promotion_discount) -
      Number(line.line_discount)
    : Number(order.subtotal) - Number(order.discount_total);

  function save() {
    apply.mutate(
      {
        orderId: order.id,
        ...(line ? { line_id: line.id } : {}),
        reason_code: code,
        kind,
        value: value.trim(),
        note: note.trim(),
      },
      { onSuccess: onDone },
    );
  }

  return (
    <Sheet
      title={line ? `Money off ${line.name}` : 'Money off this bill'}
      onDismiss={onDone}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={code === '' || value.trim() === ''}
          loading={apply.isPending}
          onClick={save}
        >
          Take it off
        </Button>
      }
    >
      {reasons.isPending ? (
        <Loading />
      ) : reasons.isError ? (
        <ErrorNotice message={reasons.error.message} />
      ) : reasons.data.length === 0 ? (
        <Empty
          title="No reasons set up"
          description="A discount is recorded against a reason. The back office keeps the list."
        />
      ) : (
        <>
          <p className="ds__base">
            Off {formatMoney(String(base.toFixed(2)))}
            {line ? ' for this dish' : ' on the bill'}
          </p>

          <Field label="Why">
            <div className="ds__reasons">
              {reasons.data.map((reason) => (
                <button
                  key={reason.id}
                  type="button"
                  className={`ds__reason${code === reason.code ? ' is-on' : ''}`}
                  onClick={() => setCode(reason.code)}
                >
                  <span>{reason.label}</span>
                  {reason.max_percent !== null && (
                    <span className="ds__cap">up to {Number(reason.max_percent)}%</span>
                  )}
                </button>
              ))}
            </div>
          </Field>

          <Field label="How much">
            <div className="ds__amount">
              <Segmented
                label="Percentage or amount"
                value={kind}
                onChange={(next) => setKind(next)}
                options={[
                  { value: 'percent', label: '%' },
                  { value: 'amount', label: 'Amount' },
                ]}
              />
              <Input
                value={value}
                inputMode="decimal"
                placeholder={kind === 'percent' ? '10' : '5.00'}
                aria-label={kind === 'percent' ? 'Percentage off' : 'Amount off'}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
          </Field>

          <Field
            label="What happened"
            hint={
              chosen?.requires_note
                ? 'Needed for this reason.'
                : 'Optional, and read by whoever asks about it later.'
            }
          >
            <Input
              value={note}
              maxLength={240}
              onChange={(e) => setNote(e.target.value)}
              placeholder={chosen?.requires_note ? 'Came out cold' : undefined}
            />
          </Field>

          <ActionError error={apply.error} action="Taking money off" title="Not taken off" />
        </>
      )}
    </Sheet>
  );
}

/** One discount on the bill, with the way to put it back. */
function DiscountRow({ orderId, discount }: { orderId: string; discount: OrderDiscount }) {
  const remove = useRemoveDiscount();
  const mayDiscount = usePermission('pricing.discount.apply');

  return (
    <div className="os__total-row os__total-row--minor os__discount">
      <span>
        {discount.reason_label}
        {discount.percent !== null && ` ${Number(discount.percent)}%`}
      </span>
      <span className="os__off">
        <span className="tabular">−{formatMoney(discount.amount)}</span>
        {mayDiscount && (
          <button
            type="button"
            className="os__off-undo"
            aria-label={`Put back ${discount.reason_label}`}
            disabled={remove.isPending}
            onClick={() => remove.mutate({ orderId, discountId: discount.id })}
          >
            Undo
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * The coupon on the tab, with what it takes off — or the server's reason it
 * takes nothing yet — and the way to take it off, which gives the code back.
 */
function CouponRow({ orderId, coupon }: { orderId: string; coupon: OrderCoupon }) {
  const remove = useRemoveCoupon();
  const mayCoupon = usePermission('pricing.coupon.redeem');
  const taking = Number(coupon.amount) > 0;

  return (
    <>
      <div className="os__total-row os__total-row--minor os__discount">
        <span>
          {coupon.name} ({coupon.code})
        </span>
        <span className="os__off">
          {coupon.kind === 'amount' && taking && (
            <span className="tabular">−{formatMoney(coupon.amount)}</span>
          )}
          {mayCoupon && (
            <button
              type="button"
              className="os__off-undo"
              aria-label={`Take off coupon ${coupon.code}`}
              disabled={remove.isPending}
              onClick={() => remove.mutate({ orderId, couponId: coupon.id })}
            >
              Take off
            </button>
          )}
        </span>
      </div>
      {coupon.note && <p className="os__coupon-note">{coupon.note}</p>}
      <ActionError error={remove.error} action="Taking the coupon off" />
    </>
  );
}

/**
 * Take a coupon code. The server says whether it is good, and why not in
 * words the waiter can read out to the guest; nothing is worked out here.
 */
function CouponSheet({ order, onDone }: { order: Order; onDone: () => void }) {
  const apply = useApplyCoupon();
  const [code, setCode] = useState('');

  return (
    <Sheet
      title="Coupon"
      onDismiss={onDone}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={code.trim() === ''}
          loading={apply.isPending}
          onClick={() =>
            apply.mutate({ orderId: order.id, code: code.trim() }, { onSuccess: onDone })
          }
        >
          Use the code
        </Button>
      }
    >
      <Field label="The code" hint="As the guest shows it. One coupon per bill.">
        <Input
          value={code}
          autoFocus
          autoCapitalize="characters"
          autoComplete="off"
          maxLength={40}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
      </Field>
      <ActionError error={apply.error} action="Taking coupons" title="Not taken" />
    </Sheet>
  );
}

/**
 * The service charge, with the way to take it off and put it back.
 *
 * Taking it off is a manager's, with a reason: it is money a rule would have
 * added and somebody decided not to, and "why" is asked afterwards. While it
 * is off the line stays, struck through, so nobody wonders where it went and
 * whoever took it off can put it back.
 */
function ServiceChargeRow({
  orderId,
  charge,
}: {
  orderId: string;
  charge: OrderServiceCharge;
}) {
  const mayRemove = usePermission('pricing.service_charge.remove');
  const restore = useRestoreServiceCharge();
  const [removing, setRemoving] = useState(false);
  const label = `${charge.name} ${Number(charge.percent)}%`;

  return (
    <>
      <div
        className={`os__total-row os__total-row--minor${charge.removed ? ' os__sc-off' : ''}`}
      >
        <span>
          <span className="os__sc-name">{label}</span>
          {charge.removed && (
            <span className="os__sc-why">
              {' '}
              — taken off{charge.removed_reason ? `: ${charge.removed_reason}` : ''}
            </span>
          )}
        </span>
        <span className="os__off">
          {!charge.removed && <span className="tabular">{formatMoney(charge.amount)}</span>}
          {mayRemove &&
            (charge.removed ? (
              <button
                type="button"
                className="os__off-undo"
                aria-label={`Put ${charge.name} back on`}
                disabled={restore.isPending}
                onClick={() => restore.mutate({ orderId })}
              >
                Put back
              </button>
            ) : (
              <button
                type="button"
                className="os__off-undo"
                aria-label={`Take ${charge.name} off`}
                onClick={() => setRemoving(true)}
              >
                Take off
              </button>
            ))}
        </span>
      </div>
      {restore.isError && (
        <ActionError error={restore.error} action="Putting it back" title="Not put back" />
      )}
      {removing && (
        <RemoveServiceChargeSheet
          orderId={orderId}
          label={label}
          amount={charge.amount}
          onDone={() => setRemoving(false)}
        />
      )}
    </>
  );
}

/** The reasons it usually comes off, as staff say them. The last opens a field. */
const SERVICE_CHARGE_REASONS = [
  'Guest unhappy with the service',
  'Long wait',
  'Guest asked',
  'House guest',
] as const;

function RemoveServiceChargeSheet({
  orderId,
  label,
  amount,
  onDone,
}: {
  orderId: string;
  label: string;
  amount: string;
  onDone: () => void;
}) {
  const remove = useRemoveServiceCharge();
  const [reason, setReason] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const chosen = reason === 'other' ? other.trim() : (reason ?? '');

  return (
    <Sheet
      title={`Take ${label} off this tab?`}
      onDismiss={onDone}
      actions={
        <>
          <Button size="lg" block onClick={onDone}>
            Keep it on
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={chosen === ''}
            loading={remove.isPending}
            onClick={() => remove.mutate({ orderId, reason: chosen }, { onSuccess: onDone })}
          >
            Take off {formatMoney(amount)}
          </Button>
        </>
      }
    >
      <p className="os__void-note">
        It stays off this tab, whatever is added to it, until somebody puts it back. Who took it
        off and why goes on the tab's history.
      </p>
      <div className="os__reasons">
        {SERVICE_CHARGE_REASONS.map((option) => (
          <button
            key={option}
            type="button"
            className={`os__reason${reason === option ? ' is-on' : ''}`}
            aria-pressed={reason === option}
            onClick={() => setReason(option)}
          >
            {option}
          </button>
        ))}
        <button
          type="button"
          className={`os__reason${reason === 'other' ? ' is-on' : ''}`}
          aria-pressed={reason === 'other'}
          onClick={() => setReason('other')}
        >
          Something else
        </button>
      </div>
      {reason === 'other' && (
        <Field label="Why">
          <Input value={other} maxLength={200} onChange={(e) => setOther(e.target.value)} />
        </Field>
      )}
      <ActionError
        error={remove.error}
        action="Taking the service charge off"
        title="Not taken off"
      />
    </Sheet>
  );
}

function CancelTabSheet({
  order,
  onDone,
  onCancelled,
}: {
  order: Order;
  onDone: () => void;
  onCancelled: () => void;
}) {
  const cancelOrder = useCancelOrder();
  const [reason, setReason] = useState<string | null>(null);
  const [other, setOther] = useState('');

  const chosen = reason === 'other' ? other.trim() : (reason ?? '');
  const live = order.lines.filter((l) => l.state !== 'voided');
  const fired = live.filter((l) => l.state === 'fired');

  // Food that has gone to the kitchen has been cooked, and the server refuses
  // to cancel a tab holding any: the cost is real and a void is what records
  // it. Said here rather than met as a refusal after the reason has been
  // picked and the red button pressed.
  if (fired.length > 0) {
    return (
      <Sheet
        title={`Tab #${order.number} is with the kitchen`}
        onDismiss={onDone}
        actions={
          <Button variant="primary" size="lg" block onClick={onDone}>
            Back to the tab
          </Button>
        }
      >
        <p className="os__void-note">
          {fired.length === 1 ? 'One line has' : `${fired.length} lines have`} been fired, so
          this tab cannot be cancelled — the food has been cooked and somebody has to account
          for it. Void the lines instead, with a reason, and the cost lands where it belongs.
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={`Cancel tab #${order.number}?`}
      tone="danger"
      onDismiss={onDone}
      actions={
        <>
          {/* Safe first, destructive last. */}
          <Button size="lg" block onClick={onDone}>
            Keep the tab
          </Button>
          <Button
            variant="danger"
            size="lg"
            block
            disabled={chosen === ''}
            loading={cancelOrder.isPending}
            onClick={() =>
              cancelOrder.mutate(
                { orderId: order.id, reason: chosen },
                { onSuccess: onCancelled },
              )
            }
          >
            Cancel tab #{order.number}
          </Button>
        </>
      }
    >
      <p className="os__void-note">
        {live.length === 0
          ? 'Nothing has been ordered on this tab, so there is nothing to void — cancelling is how it goes away.'
          : `${live.length} line${live.length === 1 ? '' : 's'} worth ${formatMoney(order.total)} go with it. Nothing has gone to the kitchen, so nothing is wasted.`}
      </p>

      <div className="os__reasons">
        {CANCEL_REASONS.map((option) => (
          <button
            key={option}
            type="button"
            className={`os__reason${reason === option ? ' is-on' : ''}`}
            aria-pressed={reason === option}
            onClick={() => setReason(option)}
          >
            {option}
          </button>
        ))}
        <button
          type="button"
          className={`os__reason${reason === 'other' ? ' is-on' : ''}`}
          aria-pressed={reason === 'other'}
          onClick={() => setReason('other')}
        >
          Something else
        </button>
      </div>

      {reason === 'other' && (
        <Field label="What happened" hint="It goes in the ledger against this tab.">
          <Input value={other} onChange={(e) => setOther(e.target.value)} autoFocus />
        </Field>
      )}

      <ActionError error={cancelOrder.error} action="Cancelling a tab" />
    </Sheet>
  );
}

const COURSES = [1, 2, 3, 4] as const;

/**
 * What the next dish tapped goes on as, said in one line over the dishes.
 *
 * It used to be every choice at once — four courses, a seat for every guest
 * and a note toggle — always on screen, for a setting most tabs never change.
 * Now it says what is set and opens the choices when tapped. Lit while the
 * next dish will ask for a note, so a waiter can see it is on.
 */
function AddingTo({
  course,
  counter,
  seat,
  seats,
  withNote,
  onOpen,
}: {
  course: number;
  /** A counter order has no courses and no seats: only the note is left. */
  counter: boolean;
  seat: number | null;
  /** How many seats there are to choose from. Nought where the plan has no seats. */
  seats: number;
  withNote: boolean;
  onOpen: () => void;
}) {
  // Only what is set is said. "Course 1 · Table" read like an instruction
  // nobody had been taught; a seat is named only once one is picked.
  const parts = counter ? [] : [`Course ${course}`];
  if (!counter && seats > 0 && seat !== null) parts.push(`Seat ${seat}`);
  if (withNote) parts.push('with a note');
  const said = parts.length > 0 ? parts.join(' · ') : 'Add a note';

  return (
    <button
      type="button"
      className={`os__adding-btn${withNote ? ' is-on' : ''}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-label={`Next dish goes on as ${said}. Change`}
    >
      {parts.length > 0 && <span className="os__adding-label">Next dish</span>}
      <span className="os__adding-value">{said}</span>
      <span aria-hidden="true">▾</span>
    </button>
  );
}

/** The choices behind that line. Each applies as it is tapped. */
function AddingSheet({
  course,
  counter,
  onCourse,
  seat,
  onSeat,
  seats,
  withNote,
  onWithNote,
  onDone,
}: {
  course: number;
  counter: boolean;
  onCourse: (course: number) => void;
  seat: number | null;
  onSeat: (seat: number | null) => void;
  seats: number;
  withNote: boolean;
  onWithNote: (on: boolean) => void;
  onDone: () => void;
}) {
  return (
    <Sheet
      title="Next dish"
      onDismiss={onDone}
      actions={
        <Button variant="primary" size="lg" block onClick={onDone}>
          Done
        </Button>
      }
    >
      <div className="os__adding">
        {!counter && (
          <div className="os__adding-row">
            <span className="os__adding-label">Course</span>
            <Segmented
              label="Course for the next dish"
              value={String(course)}
              onChange={(next) => onCourse(Number(next))}
              options={COURSES.map((c) => ({ value: String(c), label: String(c) }))}
            />
          </div>
        )}
        {!counter && seats > 0 && (
          <div className="os__adding-row">
            <span className="os__adding-label">Seat</span>
            <Segmented
              label="Seat for the next dish"
              scroll
              value={seat === null ? 'table' : String(seat)}
              onChange={(next) => onSeat(next === 'table' ? null : Number(next))}
              options={[
                // "Table" rather than blank: bread, sides and a bottle belong to
                // everybody, and a split by seat shares them out.
                { value: 'table', label: 'Shared' },
                ...Array.from({ length: seats }, (_, i) => ({
                  value: String(i + 1),
                  label: String(i + 1),
                })),
              ]}
            />
          </div>
        )}
        <div className="os__adding-row">
          <span className="os__adding-label">Note</span>
          <button
            type="button"
            className={`os__with-note${withNote ? ' is-on' : ''}`}
            aria-pressed={withNote}
            onClick={() => onWithNote(!withNote)}
          >
            With a note
          </button>
          <span className="os__adding-hint">Asks for one on the next dish only.</span>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * One course's heading: where it is, and on a plan that holds courses, the way
 * to send it when it is not the one the Send under the total already sends.
 *
 * Only dishes still waiting are counted. A course sent and then added to — a
 * forgotten side — has one new dish waiting, and the heading says so rather
 * than claiming the whole course is held.
 */
function CourseHead({
  orderId,
  group,
  mayFire,
  sendHere,
}: {
  orderId: string;
  group: CourseGroup;
  mayFire: boolean;
  /** Offer a Send on this heading: a waiting course that is not next. */
  sendHere: boolean;
}) {
  const fire = useFireCourse();
  const { state } = group;

  let said: ReactNode = null;
  if (state.kind === 'held') {
    said = (
      <span className="os__course-state is-held">
        {!mayFire
          ? 'Waiting to be sent'
          : state.all
            ? 'Not sent yet'
            : `${state.held} not sent yet`}
      </span>
    );
  } else if (state.kind === 'up') {
    said = (
      <span className="os__course-state is-up">
        <span className="up">Up</span>
        {state.readyAt !== null && <span className="tabular">{timeOf(state.readyAt)}</span>}
      </span>
    );
  } else if (state.kind === 'cooking') {
    // "0 of 1 station" is a fraction where a word will do. The count is worth
    // showing only when there is more than one station to be part-way through.
    said = (
      <span className="os__course-state is-cooking">
        {state.stations === 1
          ? 'Cooking'
          : `Cooking · ${state.stationsReady} of ${state.stations} stations`}
      </span>
    );
  } else if (state.kind === 'sent') {
    said = <span className="os__course-state">Sent</span>;
  }

  return (
    <div className="os__course-head">
      <span className="os__course-name">Course {group.courseNo}</span>
      {said && (
        <>
          <span className="os__course-dot" aria-hidden="true">
            ·
          </span>
          {said}
        </>
      )}
      {sendHere && state.kind === 'held' && mayFire && (
        <span className="os__course-side">
          <Button
            size="sm"
            loading={fire.isPending}
            onClick={() => fire.mutate({ orderId, courseNo: group.courseNo })}
            aria-label={`${state.all ? 'Send' : `Send ${state.held} more`}: course ${group.courseNo} to the kitchen`}
          >
            {state.all ? 'Send' : `Send ${state.held} more`}
          </Button>
        </span>
      )}
      {fire.isError && (
        <div className="os__course-error">
          <ActionError error={fire.error} action="Sending a course" />
        </div>
      )}
    </div>
  );
}

/**
 * The note on the whole tab.
 *
 * Seen on the floor and in the kitchen: it is printed at the top of every
 * docket for the tab, and changing it here changes the dockets already on the
 * screens. So it is the place for "birthday — candle with dessert". Something
 * about one dish still goes on that dish, where it travels with it to
 * whichever station cooks it.
 */
function TabNoteSheet({ order, onDone }: { order: Order; onDone: () => void }) {
  const setNote = useSetOrderNote();
  const [note, setNoteText] = useState(order.note ?? '');

  return (
    <Sheet
      title={`Note on #${order.number}`}
      onDismiss={onDone}
      actions={
        <>
          <Button
            variant="primary"
            size="lg"
            block
            loading={setNote.isPending}
            onClick={() => setNote.mutate({ orderId: order.id, note }, { onSuccess: onDone })}
          >
            Save note
          </Button>
          {order.note && (
            <Button
              size="lg"
              block
              disabled={setNote.isPending}
              onClick={() =>
                setNote.mutate({ orderId: order.id, note: null }, { onSuccess: onDone })
              }
            >
              Remove note
            </Button>
          )}
        </>
      }
    >
      <Field
        label="Note"
        hint="Shown on this tab and at the top of every kitchen docket for it. Something about one dish goes on that dish."
      >
        <Input
          value={note}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Birthday — candle with dessert"
          maxLength={500}
          autoFocus
        />
      </Field>
      <ActionError error={setNote.error} action="Saving the note" />
    </Sheet>
  );
}
